// Country code groups
export const EUROPEAN_COUNTRY_CODES = [
  'BE',
  'GB',
  'IE',
  'DK',
  'FR',
  'DE',
  'NL',
  'ES',
  'SE',
  'FI',
  'NO',
  'IS',
] as const;

const ANZ_COUNTRY_CODES = ['AU', 'NZ'] as const;
const NORTH_AMERICAN_COUNTRY_CODES = ['US', 'CA'] as const;
const UNKNOWN_COUNTRY_CODES = ['Unknown'] as const;

// Types for country codes
export type CountryCodeType =
  | (typeof EUROPEAN_COUNTRY_CODES)[number]
  | (typeof ANZ_COUNTRY_CODES)[number]
  | (typeof NORTH_AMERICAN_COUNTRY_CODES)[number]
  | (typeof UNKNOWN_COUNTRY_CODES)[number];

export type CountryCodeWithoutUnknownType = Exclude<CountryCodeType, 'Unknown'>;

// Next.js locales
export const NEXTJS_LOCALES = [
  'uk',
  'au',
  'nz',
  'dk',
  'ie',
  'be',
  'fr',
  'nl',
  'de',
  'es',
  'se',
  'fi',
  'no',
  'is',
  'us',
  'ca',
] as const;

export const NEXTJS_DEFAULT_LOCALE = 'uk';
export type LocaleType = (typeof NEXTJS_LOCALES)[number];

// Map country codes to locales
const COUNTRY_TO_LOCALE: Record<CountryCodeWithoutUnknownType, LocaleType> = {
  GB: 'uk',
  AU: 'au',
  NZ: 'nz',
  DK: 'dk',
  IE: 'ie',
  BE: 'be',
  FR: 'fr',
  NL: 'nl',
  DE: 'de',
  ES: 'es',
  SE: 'se',
  FI: 'fi',
  NO: 'no',
  IS: 'is',
  US: 'us',
  CA: 'ca',
} as const;

// Get a locale from a country code
export const getLocaleFromCountryCode = (countryCode: string): LocaleType => {
  if (countryCode === 'Unknown') {
    return NEXTJS_DEFAULT_LOCALE;
  }
  return (
    COUNTRY_TO_LOCALE[countryCode as CountryCodeWithoutUnknownType] ??
    NEXTJS_DEFAULT_LOCALE
  );
};

// Get a country code from a locale
export const getCountryCodeFromLocale = (
  locale: string | undefined,
): CountryCodeWithoutUnknownType => {
  if (!locale) {
    return 'GB';
  }

  return (
    (Object.keys(COUNTRY_TO_LOCALE) as CountryCodeWithoutUnknownType[]).find(
      country => COUNTRY_TO_LOCALE[country] === locale,
    ) || 'GB'
  );
};

// Map locales to language tags
export const NEXTJS_LOCALES_TO_LANG: Record<LocaleType, string> = {
  uk: 'en-GB',
  au: 'en-AU',
  nz: 'en-NZ',
  dk: 'da-DK',
  ie: 'en-IE',
  be: 'fr-BE', // n.b. Ideally, we would also map 'nl-BE' for dutch speaking Belgian users
  fr: 'fr-FR',
  nl: 'nl-NL',
  de: 'de-DE',
  es: 'es-ES',
  se: 'sv-SE',
  fi: 'fi-FI',
  no: 'no-NO',
  is: 'is-IS',
  us: 'en-US',
  ca: 'en-CA',
} as const;

export const REGIONS = [
  {
    name: 'anz',
    readableName: 'Asia/Pacific',
    countryCodes: ANZ_COUNTRY_CODES,
  },
  {
    name: 'europe',
    readableName: 'Europe',
    countryCodes: EUROPEAN_COUNTRY_CODES,
  },
  {
    name: 'north-america',
    readableName: 'North America',
    countryCodes: NORTH_AMERICAN_COUNTRY_CODES,
  },
  {
    name: 'unknown',
    readableName: 'Unknown',
    countryCodes: UNKNOWN_COUNTRY_CODES,
  },
] as const;
